import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X } from 'lucide-react';

const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirmar Acción',
    message = '¿Estás seguro de que deseas realizar esta acción?',
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    variant = 'danger' // 'danger' | 'warning' | 'info'
}) => {
    const getVariantColors = () => {
        switch (variant) {
            case 'danger':
                return {
                    icon: 'text-red-500',
                    bg: 'bg-red-500/10',
                    button: 'bg-red-600 hover:bg-red-500 shadow-red-900/20'
                };
            case 'warning':
                return {
                    icon: 'text-amber-500',
                    bg: 'bg-amber-500/10',
                    button: 'bg-amber-600 hover:bg-amber-500 shadow-amber-900/20'
                };
            default:
                return {
                    icon: 'text-blue-500',
                    bg: 'bg-blue-500/10',
                    button: 'bg-blue-600 hover:bg-blue-500 shadow-blue-900/20'
                };
        }
    };

    const colors = getVariantColors();

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm transition-colors"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl shadow-2xl p-6 overflow-hidden transition-colors"
                    >
                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
                        >
                            <X size={20} />
                        </button>

                        <div className="flex flex-col items-center text-center space-y-4">
                            {/* Icon Circle */}
                            <div className={`p-4 rounded-full ${colors.bg} ${colors.icon}`}>
                                <AlertCircle size={32} />
                            </div>

                            <div className="space-y-2">
                                <h3 className="text-xl font-bold text-zinc-900 dark:text-white">
                                    {title}
                                </h3>
                                <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed">
                                    {message}
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 w-full pt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 px-4 py-3 rounded-xl text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white font-bold hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all border border-transparent hover:border-gray-200 dark:hover:border-zinc-700"
                                >
                                    {cancelText}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onConfirm();
                                        onClose();
                                    }}
                                    className={`flex-1 px-4 py-3 rounded-xl text-white font-bold shadow-lg transition-all transform active:scale-95 ${colors.button}`}
                                >
                                    {confirmText}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ConfirmModal;
